import {
  blockLimitedHoldingsSubjectsWithFees,
  blockSettings,
  convertStudioSubjectToHoldingsSubject,
  holdingsCategories,
  convertHoldingsBlockTypeToBreakdown,
  proxySetIdState,
} from 'venn-state';
import { logExceptionIntoSentry, useQuery, createHoldingsBreakdownCachedKey } from 'venn-utils';
import { useRecoilValue } from 'recoil';
import { type HoldingsDataRequest, type HoldingsDataResponse, type HoldingsSubject } from 'venn-api';
import { getHoldingsBreakdown } from 'venn-api';
import type { CustomBlockTypeEnum } from 'venn-utils';

const ONE_HOUR = 1000 * 60 * 60;

export const useHoldings = (id: string) => {
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(id));
  const settings = useRecoilValue(blockSettings(id));
  const blockType = settings.customBlockType;
  const categories = useRecoilValue(holdingsCategories(id));
  const proxySetId = useRecoilValue(proxySetIdState);

  // holdingsSubjects doesn't need memo since useQuery uses deep equality anyway
  const holdingsSubjects = allSubjects.map(convertStudioSubjectToHoldingsSubject);

  const holdingsFetchingFunction = async (
    blockType: CustomBlockTypeEnum,
    holdingsSubjects: HoldingsSubject[],
    proxySetId: string | undefined,
  ): Promise<HoldingsDataResponse> => {
    const breakdownType = convertHoldingsBlockTypeToBreakdown(blockType);
    const request: HoldingsDataRequest = {
      breakdownType,
      subjects: holdingsSubjects,
      proxySetId,
    } as HoldingsDataRequest;

    const { content } = await getHoldingsBreakdown(request);
    return content;
  };

  const { data } = useQuery<HoldingsDataResponse>(
    createHoldingsBreakdownCachedKey(blockType, holdingsSubjects, proxySetId),
    () => {
      return holdingsFetchingFunction(blockType, holdingsSubjects, proxySetId);
    },
    {
      onError: (error) => {
        logExceptionIntoSentry(error as Error);
      },
      suspense: true,
      staleTime: ONE_HOUR,
    },
  );

  return { data, categories };
};
